import { parseCurrency } from "../../utils/currency";
import {
  FeatureEnum,
  GroupIdEnum,
  ModuleEnum,
  OwnerEnum,
} from "app/modules/Segment/types";
import BaseTrackingService from "./BaseTrackingService";
import { handleAxiosErrorMessage } from "./customErrors/apiErrors";

const JUSPROCESSOS_EVENT_CONTEXT = componente => ({
  groupId: GroupIdEnum.JUSPROCESSOS_GROUP,
  library: {
    name: "jusprocessos-library",
    componente,
  },
});

/**
 * Class de track de eventos de upgrade de plano em jusProcessos
 * jus_processos_product_lp_checkout_opened
 * jus_processos_product_lp_checkout_payment_step_opened
 * jus_processos_subscription_made
 * jus_processos_subscription_error
 * save_card_api_failed
 */
class JusProcessosSubscriptionTrackingService extends BaseTrackingService {
  static EVENT_CHECKOUT_OPENED = 'jus_processos_product_lp_checkout_opened';
  static EVENT_PAYMENT_STEP_OPENED = 'jus_processos_product_lp_checkout_payment_step_opened';
  static EVENT_SUBSCRIPTION_MADE = 'jus_processos_subscription_made';
  static EVENT_SUBSCRIPTION_CLICKED = 'jus_processos_subscription_sign_now_button_clicked';
  static EVENT_SUBSCRIPTION_ERROR = 'jus_processos_subscription_error';
  static EVENT_SAVE_CARD_ERROR = 'save_card_api_failed';

  constructor() {
    super("JusProcessosSubscriptionsTrackingService");
  }

  _buildBasicData(userData, currentPlan) {
    return {
      ...this._buildSegmentUserEventProperties(userData),

      // Plano atual
      current_plan_id: currentPlan?.plan_details?.id
        ?? currentPlan?.subscription_status?.plan_details?.id,
      current_plan_name: currentPlan?.plan_details?.name
        ?? currentPlan?.subscription_status?.plan_details?.name,
      current_plan_amount: currentPlan?.plan_details?.amount
        ?? currentPlan?.subscription_status?.plan_details?.amount,
      current_plan_type: currentPlan?.plan_details?.plan_type
        ?? currentPlan?.subscription_status?.plan_details?.plan_type,
    };
  }

   /**
   * Método para rastrear abertura do checkout
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} selectedPlan - Plano selecionado
   */
  trackCheckoutOpened(userData, currentPlan) {
    const trackingData = {
      ...this._buildBasicData(userData, currentPlan),
      owner: OwnerEnum.GDC,
      feature: FeatureEnum.JUS_PROCESSOS,
      module: ModuleEnum.JUS_PROCESSOS,
      event_meaning:
        "Ao clicar no botão de contratar/ver planos — abre o modal de seleção de quantidade de processos",
      relevant_user_activity: false,
      provider: currentPlan?.info?.provider || "unknown",
      // selected_plan_id: selectedPlan?.id,
      // selected_plan_name: selectedPlan?.name,
      // selected_plan_amount: selectedPlan?.amount,
      // selected_plan_type: selectedPlan?.plan_type,
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_CHECKOUT_OPENED,
      step: 'checkout_opened',
      context: 'jusprocessos'
    };

    this._trackEvent(
      this.constructor.EVENT_CHECKOUT_OPENED,
      trackingData,
      JUSPROCESSOS_EVENT_CONTEXT("button-component"),
    );
  }

  /**
   * Método para rastrear abertura do passo de pagamento
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} selectedPlan - Plano selecionado
   */
  trackPaymentStepOpened(userData, currentPlan, selectedPlan) {
    const trackingData = {
      ...this._buildBasicData(userData, currentPlan),
      owner: OwnerEnum.GDC,
      feature: FeatureEnum.JUS_PROCESSOS,
      module: ModuleEnum.JUS_PROCESSOS,
      event_meaning:
        "Ao clicar no botão de contratar/ver planos — abre o modal de seleção de quantidade de processos",
      relevant_user_activity: false,
      provider: currentPlan?.info?.provider || "unknown",
      selected_plan_id: selectedPlan?.id,
      selected_plan_name: selectedPlan?.name,
      selected_plan_amount: parseCurrency(selectedPlan?.fullPrice),
      selected_plan_type: selectedPlan?.plan_type,
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_STEP_OPENED,
      step: 'payment_opened',
      context: 'jusprocessos'
    };

    this._trackEvent(
      this.constructor.EVENT_PAYMENT_STEP_OPENED,
      trackingData,
      JUSPROCESSOS_EVENT_CONTEXT("modal-component"),
    );
  }

  /**
   * Método para rastrear sucesso na assinatura
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {Object} selectedPlan - Plano selecionado
   * @param {Object} subscriptionData - Dados da assinatura
   */
  trackSubscriptionMade(userData, currentPlan, selectedPlan, subscriptionData) {
    const trackingData = {
      ...this._buildBasicData(userData, currentPlan),
      owner: OwnerEnum.GDC,
      feature: FeatureEnum.JUS_PROCESSOS,
      module: ModuleEnum.JUS_PROCESSOS,
      event_meaning: "Ao finalizar uma assinatura com sucesso no checkout",
      relevant_user_activity: true,
      provider: subscriptionData?.provider ||
        subscriptionData?.subscription_status?.info?.provider ||
        currentPlan?.subscription_status?.info?.provider ||
        "unknown",
      selected_plan_id: selectedPlan?.id,
      selected_plan_name: selectedPlan?.name,
      selected_plan_amount: parseCurrency(selectedPlan?.fullPrice),
      selected_plan_type: selectedPlan?.plan_type,
      subscription_id: subscriptionData?.subscription_id,
      payment_method: subscriptionData?.payment_method,
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_SUBSCRIPTION_MADE,
      step: 'subscription_completed',
      context: 'jusprocessos'
    };

    this._trackEvent(
      this.constructor.EVENT_SUBSCRIPTION_MADE,
      trackingData,
      JUSPROCESSOS_EVENT_CONTEXT("modal-component"),
    );
  }

  /**
   * Monta propriedades para `trackJusProcessosSubscriptionSignNowButtonClicked` em trackEvents.ts.
   */
  prepareJusProcessosSubscriptionSignNowButtonClickedForAnalytics(
    userData,
    currentPlan,
    selectedPlan,
    card,
  ) {
    return {
      ...this._buildBasicData(userData, currentPlan),
      provider:
        currentPlan?.subscription_status?.info?.provider ||
        'unknown',
      selected_plan_id: selectedPlan?.id,
      selected_plan_name: selectedPlan?.name,
      selected_plan_amount: parseCurrency(selectedPlan?.fullPrice),
      selected_plan_type: selectedPlan?.plan_type,
      payment_method: card,
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_SUBSCRIPTION_CLICKED,
      step: 'subscription_clicked',
      context: 'jusprocessos',
    };
  }

  /**
   * Monta propriedades para `trackJusProcessosSubscriptionError` em trackEvents.ts.
   */
  prepareJusProcessosSubscriptionErrorForAnalytics(
    userData,
    currentPlan,
    selectedPlan,
    errorMessage,
  ) {
    const { message = null, data = null, status = null } =
      handleAxiosErrorMessage(errorMessage);

    return {
      ...this._buildBasicData(userData, currentPlan),
      provider:
        currentPlan?.subscription_status?.info?.provider ||
        currentPlan?.info?.provider ||
        'unknown',
      selected_plan_id: selectedPlan?.id,
      selected_plan_name: selectedPlan?.name,
      selected_plan_amount: parseCurrency(selectedPlan?.fullPrice),
      selected_plan_type: selectedPlan?.plan_type,
      error_message: message,
      error_data: data,
      error_status: status,
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_SUBSCRIPTION_ERROR,
      step: 'subscription_error',
      context: 'jusprocessos',
    };
  }

  /**
   * Método para rastrear erro ao salvar cartão
   * @param {Object} userData - Dados do usuário
   * @param {Object} currentPlan - Plano atual
   * @param {String} errorMessage - Mensagem de erro
   */
  trackSaveCardError(userData, currentPlan, errorMessage) {
    const { message, status = null, data = null } = handleAxiosErrorMessage(errorMessage);
    const trackingData = {
      ...this._buildBasicData(userData, currentPlan),
      owner: OwnerEnum.GDC,
      feature: FeatureEnum.JUS_PROCESSOS,
      module: ModuleEnum.JUS_PROCESSOS,
      event_meaning: "Ao falhar o salvamento do cartão no checkout do JusProcessos",
      relevant_user_activity: false,
      provider: currentPlan?.info?.provider || "unknown",
      error_message: message,
      error_data: data,
      error_status: status,
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_SAVE_CARD_ERROR,
      step: 'save_card_error',
      context: 'jusprocessos'
    };

    this._trackEvent(
      this.constructor.EVENT_SAVE_CARD_ERROR,
      trackingData,
      JUSPROCESSOS_EVENT_CONTEXT("modal-component"),
    );
  }
}

export default new JusProcessosSubscriptionTrackingService(); // FIX export object instances
